"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
} from "@/components/feature-template/types";
import { Bot, CreditCard, LifeBuoy } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/resources/faq";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    title: "Set Up MindMyJob in a Few Minutes",
    description:
      "Install the extension, connect your job boards, complete your profile and send your first application.",
    imageSrcUrl: `${IMG}/hero-getting-started.png`,
    primaryActionText: "See How to Use",
    primaryActionLink: "/how-to-use",
    secondaryActionText: "Create Free Account",
    secondaryActionLink: "/sign-up",
  },
  {
    id: "ai-agents",
    name: "AI Agents",
    title: "How the AI Agents Work",
    description:
      "What Auto Apply, Live Apply, Tailored Apply and Inbox Apply each do, and the controls that keep you in charge.",
    imageSrcUrl: `${IMG}/hero-ai-agents.png`,
    primaryActionText: "Explore AI Agents",
    primaryActionLink: "/ai-agents",
    secondaryActionText: "See How to Use",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "billing",
    name: "Billing & Plans",
    title: "Plans, Credits and Billing",
    description:
      "What each plan includes, how credits work, and how to upgrade, downgrade or cancel from your account.",
    imageSrcUrl: `${IMG}/hero-billing.png`,
    primaryActionText: "Compare Plans",
    primaryActionLink: "/pricing",
    secondaryActionText: "Contact Support",
    secondaryActionLink: "/support",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "platform-ai-agents",
    title: "Platform and AI Agents",
    subtitle: "HOW IT WORKS",
    description:
      "How the agents choose jobs, how resumes are tailored, when you review before anything is sent and which job boards are supported. Search a question and get a straight answer.",
    benefits: [
      "How agents pick jobs",
      "Review mode and limits",
      "Resume tailoring explained",
      "Supported job boards",
    ],
    primaryCTA: "Explore AI Agents",
    primaryLink: "/ai-agents",
    secondaryCTA: "See How to Use",
    secondaryLink: "/how-to-use",
    imageSrcUrl: `${IMG}/help-center.png`,
    icon: <Bot className="w-4 h-4" />,
    stats: "Plain-English answers",
  },
  {
    id: "billing-account",
    title: "Billing and Account",
    subtitle: "PLANS AND PAYMENTS",
    description:
      "Change your plan, update your payment method and download invoices from your account settings. Cancel any time and keep access until the end of the period you paid for.",
    benefits: [
      "Upgrade or downgrade any time",
      "Usage shown for each cycle",
      "Downloadable invoices",
      "Cancel from your account",
    ],
    primaryCTA: "Compare Plans",
    primaryLink: "/pricing",
    secondaryCTA: "Contact Support",
    secondaryLink: "/support",
    imageSrcUrl: `${IMG}/billing-account.png`,
    icon: <CreditCard className="w-4 h-4" />,
    stats: "Manage it yourself",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "support",
    title: "Still Stuck? Talk to Our Team",
    subtitle: "REAL PEOPLE, QUICK REPLIES",
    description:
      "Can't find your answer? Send us a message from the Support page and a member of the team will reply, usually within the same working day.",
    benefits: [
      "Setup and connection help",
      "Plans, credits and invoices",
      "Account and data requests",
      "Bugs and feature requests",
    ],
    primaryCTA: "Contact Support",
    primaryLink: "/support",
    secondaryCTA: "Back to Resources",
    secondaryLink: "/resources",
    imageSrcUrl: `${IMG}/support.png`,
    icon: <LifeBuoy className="w-4 h-4" />,
    stats: "Replies from real people",
  },
];

export default function FAQPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Answers to",
            titleHighlight: "Common Questions.",
            subtitle:
              "How MindMyJob works, what the AI agents do, plans and billing, and how to reach our team when you need help.",
            ctaText: "See How to Use",
            ctaLink: "/how-to-use",
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Find Your Answer Fast.",
            sectionSubtitle: "Browse by topic, or ask our team directly.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "faq",
          data: {
            eyebrow: "ALL QUESTIONS",
            title: "Frequently Asked Questions",
            subtitle: "Setup, AI agents, privacy, plans and support.",
            faqs,
            ctaTitle: "Didn't find your answer?",
            ctaText: "Contact Support",
            ctaLink: "/support",
          },
        },
      ]}
    />
  );
}
